// WebRTCContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { SocketContext } from './SocketContext';
import ImageLoadingManager from "../components/recordings/ImageLoadingManager";

export const WebRTCContext = createContext();


class DatachannelMsgHandler {

    constructor() {
        this.datachannel = null;
        this.msgListener = null;
    }

    setDataChannel(dataChannel) {
        this.datachannel = dataChannel;
    }

    setMsgListener(msgListener) {
        console.log('Commands channel datachannelMsgHandler setMsgListener: ', this, msgListener);
        this.msgListener = msgListener;
    }

    clearMsgListener() {
        this.msgListener = null;
    }

    sendDataChnlCommand(commandMsg) {
        if (this.datachannel) {
            this.datachannel.send(commandMsg);
        } else {
            console.log(`Datachannel is not available to send the command`);
        }
    }

    handleDatachannelMsg(msg) {
        console.log("Data message:", msg);
        try {
            const parsedJSON = JSON.parse(msg);
            if (this.msgListener) {
                this.msgListener.onmessage(parsedJSON)
            } else {
                console.warn("No listeners for incoming datachannel message");
            }
        } catch (error) {
            console.error("Can't parse incoming datachannel command as JSON", error);
        }
    }

    handleDatachannelOpen() {
        console.log("Data message opened for DatachannelMsgHandler");
    }

    handleDatachannelClose() {
        console.log("Data message closed for DatachannelMsgHandler");
        this.clear()
    }

    clear() {
        this.datachannel = null;
        this.msgListener = null;
    }
}

/**
 * A class that encapsulates your WebRTC session logic.
 * (Adapted from your monolithic code.)
 */
class WebRTCSession {

    COMMANDS_DATA_CHNL = "commands-channel"
    IMAGES_DATA_CHNL = "images-channel"

    constructor(apiHandler) {
        this.apiHandler = apiHandler;
        this.socket = apiHandler.socket; // Assume socket is available from the apiHandler
        this.remoteIceCandidatesBuffer = [];
        this.localPeerConnection = null;
        this.commandsDataChannel = null;
        this.datachannelMsgHandler = new DatachannelMsgHandler()
        this.imagesDataChannel = null;
        this.imageLoadingManager = new ImageLoadingManager();
        this.remoteStream = null; // Will hold the remote MediaStream

        // ICE server configuration and constraints
        this.servers = {
            iceServers: [
                {
                    urls: 'turn:54.93.243.222:3478',
                    username: 'dummyuser',
                    credential: 'dummypass',
                },
            ],
        };

        this.pcConstraints = {
            optional: [{ DtlsSrtpKeyAgreement: true }],
        };
    }

    /**
     * Initiates the session negotiation process.
     */
    startNegotiation() {
        console.log('startNegotiation invoked');

        // Create the RTCPeerConnection instance
        this.localPeerConnection = new RTCPeerConnection(this.servers, this.pcConstraints);

        // ICE candidate handler
        this.localPeerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                this.sendLocalCandidate(event.candidate);
            }
        };

        // Remote track handler
        this.localPeerConnection.ontrack = (event) => {
            console.log('gotRemoteTrack invoked', event);
            this.handleRemoteTrack(event);
        };

        // Handle incoming data channels if needed
        this.localPeerConnection.ondatachannel = (event) => {
            var channel = event.channel;
            var label = channel.label
            console.log('New data channel: ', label);
            if (this.COMMANDS_DATA_CHNL === label) {
                this.handleCommandsDataChannel(channel);
            } else if (this.IMAGES_DATA_CHNL === label) {
                this.handleImagesDataChannel(channel);
            } else {
                console.log('Unknown data channel: ', label);
            }
        };

        // Listen for connection state changes
        this.localPeerConnection.onconnectionstatechange = () => {
            console.log('Peer connection state changed:', this.localPeerConnection.connectionState);
            if (this.localPeerConnection.connectionState === 'closed' ||
                this.localPeerConnection.connectionState === 'disconnected') {
                console.log('WebRTC connection is closed');
            }
        };

        // Start negotiation by sending a negotiation request over the signaling channel
        this.sendNegotiationRequest();
    }

    handleCommandsDataChannel(channel) {
        this.commandsDataChannel = channel;
        this.datachannelMsgHandler.setDataChannel(this.commandsDataChannel)
        this.commandsDataChannel.onopen = () => {
            console.log('Commands channel is open');
            this.datachannelMsgHandler?.handleDatachannelOpen();
        };
        this.commandsDataChannel.onclose = () => {
            console.log('Commands channel is closed');
            this.datachannelMsgHandler?.handleDatachannelClose();
        };
        this.commandsDataChannel.onmessage = (event) => {
            console.log('Commands channel message:', event.data);
            this.datachannelMsgHandler?.handleDatachannelMsg(event.data);
        };
    }

    handleImagesDataChannel(channel) {
        this.imagesDataChannel = channel;
        this.imagesDataChannel.binaryType = 'arraybuffer';
        this.imageLoadingManager.setImagesDataChannel(channel);
        this.imagesDataChannel.onopen = () => {
            console.log('Images channel is open');
        };
        this.imagesDataChannel.onclose = () => {
            console.log('Images channel is closed');
        };
    }

    sendNegotiationRequest() {
        console.log('Sending negotiation request');
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.apiHandler.setWebRTCCallback({
                on_remote_offer: (offer) => {
                    console.log("WebRTC handling of remote offer:", offer);
                    this.handleRemoteDescription(offer)
                },
                on_remote_ice_candidates: (ices, type) => {
                    console.log("WebRTC handling of remote ICEs:", ices);
                    this.handleRemoteIceCandidates(ices, type)
                }
            })

            const msg = {
                method: 'stream_control',
                type: 'negotiate',
            };
            this.socket.send(JSON.stringify(msg));
            console.log('Negotiation request sent:', msg);
        }
    }

    handleLocalDescription(answer){
        console.log('gotLocalDescription invoked:', answer);
        this.localPeerConnection.setLocalDescription(answer);
        while (this.remoteIceCandidatesBuffer.length > 0) {
            const iceCandidate = this.remoteIceCandidatesBuffer.pop();
            console.log("Handling:", iceCandidate); // Process the item here
            this.localPeerConnection.addIceCandidate(iceCandidate)
        }
        this.sendLocalDescription(answer);
    }

    sendLocalDescription(answer) {
        console.log('sendLocalDescription invoked', answer);
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            const msg = {
                method: 'sdp',
                type: 'answer',
                sdp: answer.sdp
            };
            this.socket.send(JSON.stringify(msg));
            console.log('Answer sent:', answer);
        }
    }

    sendLocalCandidate(candidate) {
        console.log('gotLocalIceCandidateOffer invoked', candidate);
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            const msg = {
                method: 'candidate',
                type: 'add',
                ices: [candidate]
            };
            this.socket.send(JSON.stringify(msg));
            console.log('Candidate sent:', candidate);
        }
    }

    handleRemoteDescription(offer) {
        console.log('handleRemoteDescription invoked:', offer);
        this.localPeerConnection.setRemoteDescription(offer).then(r => {
            console.log('remoteDescription set:', r);
            this.localPeerConnection.createAnswer().then(answer => this.handleLocalDescription(answer));
        });
    }

    handleRemoteIceCandidates(iceCandidates, type) {
        if (this.localPeerConnection.localDescription) {
            if (this.localPeerConnection.remoteDescription) {
                if (type === 'add') {
                    iceCandidates.forEach( (candidate) => {
                        this.localPeerConnection.addIceCandidate(candidate);
                    })
                } else if (type === 'remove') {
                    this.localPeerConnection.removeIceCandidates(iceCandidates);
                }

            } else {
                if (type === 'add') {
                    this.remoteIceCandidatesBuffer.push(iceCandidates)
                } else if (type === 'remove') {
                    this.remoteIceCandidatesBuffer = this.remoteIceCandidatesBuffer.filter(item => !iceCandidates.includes(item));
                }
            }
        }
    }

    handleRemoteTrack(event) {
        if (event.streams && event.streams.length > 0) {
            this.remoteStream = event.streams[0];
            console.log('Remote stream received, streams count:', event.streams.length);
        } else if (event.track) {
            this.remoteStream = new MediaStream([event.track]);
            console.log('Remote stream created from track:', event.track.label);
        } else {
            console.log('Remote stream should be created from track. But it doesn\'t');
        }
        // Here you can call a callback to update your UI (e.g., assign remoteStream to a video element).
    }

    close() {
        if (this.localPeerConnection) {
            this.localPeerConnection.getSenders().forEach((sender) => {
                if (sender.track) {
                    sender.track.stop();
                }
            });
            this.localPeerConnection.close();
            this.localPeerConnection = null;
            console.log('WebRTC session closed');
        }
    }
}

/**
 * Helper function to create a new WebRTCSession and start negotiation.
 */
function createWebRTCSession(apiHandler) {
    console.log('Initializing WebRTC session with:', apiHandler);
    const session = new WebRTCSession(apiHandler);
    session.startNegotiation();
    return session;
}

export const WebRTCProvider = ({ children }) => {
    const { socket, isConnected, apiHandler, isPeerConnected } = useContext(SocketContext);
    const [rtcSession, setRtcSession] = useState(null);

    useEffect(() => {
        console.log('WebRTCProvider useEffect: Checking socket & API handler availability');
        // Only start the WebRTC session when:
        // - The socket is connected,
        // - The API handler is available,
        // - A peer is connected (isPeerConnected is true),
        // - And no session exists yet.
        if (isConnected && socket && apiHandler && isPeerConnected && !rtcSession) {
            console.log('Dependencies are ready; starting WebRTC session negotiation');
            const session = createWebRTCSession(apiHandler);
            setRtcSession(session);
        } else if ((!isPeerConnected || !isConnected || !apiHandler) && rtcSession) {
            // If dependencies become unavailable (e.g. peer disconnects), close the session.
            rtcSession.close();
            setRtcSession(null);
        }
    }, [isConnected, socket, apiHandler, isPeerConnected, rtcSession]);

    // Clean up the session when the provider unmounts.
    useEffect(() => {
        return () => {
            if (rtcSession) {
                rtcSession.close();
            }
        };
    }, [rtcSession]);

    return (
        <WebRTCContext.Provider value={{ rtcSession }}>
            {children}
        </WebRTCContext.Provider>
    );
};
